import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, AlertCircle, AlertTriangle, Info, X } from 'lucide-react';
import styles from './Toast.module.css';
import {
  ToastContext,
  type ToastContextValue,
  type ToastOptions,
  type ToastType,
} from './toastContext';

interface ToastInstance extends Required<Pick<ToastOptions, 'title' | 'type'>> {
  id: number;
  message?: string;
  durationMs: number;
  leaving: boolean;
}

const MAX_VISIBLE = 4;
const MIN_DURATION_MS = 4000;

function defaultDuration(type: ToastType): number {
  if (type === 'error') return Number.POSITIVE_INFINITY;
  if (type === 'warning') return 8000;
  return 5000;
}

function iconFor(type: ToastType): ReactNode {
  const props = { size: 20, strokeWidth: 1.5, 'aria-hidden': true } as const;
  switch (type) {
    case 'success': return <CheckCircle {...props} />;
    case 'error':   return <AlertCircle {...props} />;
    case 'warning': return <AlertTriangle {...props} />;
    case 'info':    return <Info {...props} />;
  }
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastInstance[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((curr) => curr.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    // Remove from DOM after the leaving animation (150 ms — matches CSS).
    window.setTimeout(() => {
      setToasts((curr) => curr.filter((t) => t.id !== id));
    }, 160);
  }, []);

  const show = useCallback(
    (opts: ToastOptions): number => {
      const id = nextId.current++;
      const type: ToastType = opts.type ?? 'info';
      const requested = opts.durationMs ?? defaultDuration(type);
      const duration =
        type === 'error' ? Number.POSITIVE_INFINITY : Math.max(MIN_DURATION_MS, requested);

      setToasts((curr) => {
        const next = [
          ...curr,
          { id, type, title: opts.title, message: opts.message, durationMs: duration, leaving: false },
        ];
        // Oldest auto-dismissed when a 5th arrives (spec §0.6.11).
        if (next.length > MAX_VISIBLE) {
          const overflow = next.slice(0, next.length - MAX_VISIBLE);
          overflow.forEach((t) => dismiss(t.id));
        }
        return next;
      });

      if (Number.isFinite(duration)) {
        window.setTimeout(() => dismiss(id), duration);
      }
      return id;
    },
    [dismiss],
  );

  const value = useMemo<ToastContextValue>(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div
          className={styles.container}
          role="region"
          aria-label="Notifications"
          aria-live="polite"
        >
          {toasts.map((t) => (
            <ToastItem key={t.id} toast={t} onClose={() => dismiss(t.id)} />
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

function ToastItem({ toast, onClose }: { toast: ToastInstance; onClose: () => void }) {
  const typeClass = styles[toast.type] ?? '';
  // Inline style is reserved for *dynamic* values per spec §0.11 rule #1.
  // The animation-duration is per-toast (varies by type) — qualifies as dynamic.
  const progressStyle = Number.isFinite(toast.durationMs)
    ? { animationDuration: `${toast.durationMs}ms` }
    : undefined;

  return (
    <div
      className={`${styles.toast} ${typeClass} ${toast.leaving ? styles.leaving ?? '' : ''}`}
      role="alert"
    >
      <span className={styles.icon}>{iconFor(toast.type)}</span>
      <div className={styles.body}>
        <p className={styles.title}>{toast.title}</p>
        {toast.message ? <p className={styles.message}>{toast.message}</p> : null}
      </div>
      <button
        type="button"
        className={styles.closeBtn}
        onClick={onClose}
        aria-label="Dismiss notification"
      >
        <X size={14} strokeWidth={1.5} aria-hidden="true" />
      </button>
      {progressStyle ? <span className={styles.progress} style={progressStyle} /> : null}
    </div>
  );
}

